import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getReceiptTemplate, saveReceiptTemplate, uploadReceiptLogo, getBranches, getAssetUrl } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import FloatingTextarea from "../components/UI/FloatingTextarea";
import SelectBox from "../components/UI/SelectBox";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

const DEFAULT_FORM = {
  show_logo: false,
  logo_path: "",
  header_text: "",
  footer_text: "",
  show_tax_breakdown: true,
  show_cashier_name: true,
  show_customer_info: true,
  show_barcode: false,
  show_order_number: true,
  show_invoice_number: true,
  show_date_time: true,
  label_subtotal: "",
  label_total: "",
  label_payments: "",
  font_size: "normal",
  paper_width_mm: "80",
};

// These four strings are what services.RenderReceiptESCPOS on the backend
// actually falls back to when the matching template field is left blank
// (see defaultLabelSubtotal/defaultLabelTotal/defaultLabelPayments and the
// hardcoded "Thank you!" in receipt_escpos.go) — kept as plain literals
// here, deliberately NOT run through t(), because the preview's whole job
// is to mirror the real bytes a printer would receive. Translating them
// would make the preview lie about what the printer actually prints (it
// always falls back to this exact English text, regardless of the admin's
// own UI language).
const PRINTED_DEFAULT_SUBTOTAL = "Subtotal";
const PRINTED_DEFAULT_TOTAL = "TOTAL";
const PRINTED_DEFAULT_PAYMENTS = "Payments";
const PRINTED_DEFAULT_FOOTER = "Thank you!";

function money(n) {
  return n.toFixed(2);
}

// ReceiptPreview mirrors, line for line, the exact order
// services.RenderReceiptESCPOS renders in on the backend (branch name →
// header → order/invoice/date → cashier/customer → items → totals →
// payments → barcode → footer) against one fixed, fake sample order — a
// live, client-side approximation of the real printed bytes with zero
// server round-trips, so every toggle/label/logo change is reflected the
// instant it's changed.
function ReceiptPreview({ form, logoPreviewUrl, t }) {
  const large = form.font_size === "large";
  const widthPx = form.paper_width_mm === "58" ? 260 : 340;
  const baseTextClass = large ? "text-[17px] leading-[1.55] font-semibold" : "text-[12.5px] leading-[1.45]";

  const sample = {
    branch: t("preview_branch_name", "Sample Branch"),
    orderNumber: 1234,
    invoiceNumber: "INV-1004",
    date: new Date().toLocaleString(),
    cashier: t("preview_cashier_name", "John Cashier"),
    customerName: t("preview_customer_name", "Jane Customer"),
    customerPhone: "+1 555 0100",
    items: [
      { name: t("preview_item_1", "Sample Product"), qtyLabel: "2", unitPrice: 25, lineTotal: 50 },
      { name: t("preview_item_2", "Another Item"), qtyLabel: "1", unitPrice: 15, lineTotal: 15 },
    ],
    subtotal: 65,
    discount: 5,
    tax: 6,
    total: 66,
    paymentMethod: t("preview_payment_method", "Cash"),
    // Sample cash tender with change due, so the preview always shows the
    // "Received / Change" lines RenderReceiptESCPOS prints for a real cash
    // sale with change owed (see receipt_escpos.go's own Payments loop) —
    // otherwise this preview would silently omit the exact thing the user
    // reported missing from real printed receipts.
    received: 70,
    change: 4,
    currency: "USD",
  };

  const Row = ({ left, right, bold }) => (
    <div className={`flex justify-between gap-2 ${bold ? "font-bold" : ""}`}>
      <span className="truncate">{left}</span>
      <span className="shrink-0">{right}</span>
    </div>
  );
  const Rule = () => <div className="border-b-2 border-dashed border-gray-300 my-1.5" />;

  return (
    <div className="bg-gray-50 rounded-2xl p-4 flex justify-center">
      <div
        className={`bg-white shadow-md px-4 py-5 font-mono ${baseTextClass}`}
        style={{ width: widthPx }}
      >
        {form.show_logo && logoPreviewUrl && (
          <div className="flex justify-center mb-2">
            <img src={logoPreviewUrl} alt="Logo" className="max-h-16 object-contain" />
          </div>
        )}

        <div className="text-center font-bold">{sample.branch}</div>
        {form.header_text
          ?.split("\n")
          .filter(Boolean)
          .map((line, i) => (
            <div key={i} className="text-center">{line}</div>
          ))}
        <div className="h-2" />

        {form.show_order_number && <div>Order #{sample.orderNumber}</div>}
        {form.show_invoice_number && <div>Invoice {sample.invoiceNumber}</div>}
        {form.show_date_time && <div>{sample.date}</div>}
        {form.show_cashier_name && <div>Cashier: {sample.cashier}</div>}
        {form.show_customer_info && (
          <>
            <div>Customer: {sample.customerName}</div>
            <div>Phone: {sample.customerPhone}</div>
          </>
        )}
        <Rule />

        {sample.items.map((item, i) => (
          <div key={i} className="mb-1">
            <div>{item.name}</div>
            <Row left={`  ${item.qtyLabel} x ${money(item.unitPrice)}`} right={`${money(item.lineTotal)} ${sample.currency}`} />
          </div>
        ))}
        <Rule />

        <Row left={form.label_subtotal || PRINTED_DEFAULT_SUBTOTAL} right={`${money(sample.subtotal)} ${sample.currency}`} />
        <Row left="Discount" right={`-${money(sample.discount)} ${sample.currency}`} />
        {form.show_tax_breakdown && <Row left="Tax" right={`${money(sample.tax)} ${sample.currency}`} />}
        <Row left={form.label_total || PRINTED_DEFAULT_TOTAL} right={`${money(sample.total)} ${sample.currency}`} bold />
        <div className="h-2" />

        <div>{(form.label_payments || PRINTED_DEFAULT_PAYMENTS) + ":"}</div>
        <Row left={`  ${sample.paymentMethod}`} right={`${money(sample.total)} ${sample.currency}`} />
        <Row left="    Received" right={`${money(sample.received)} ${sample.currency}`} />
        <Row left="    Change" right={`${money(sample.change)} ${sample.currency}`} />
        <div className="h-2" />

        {form.show_barcode && (
          <div className="border border-gray-400 text-center py-3 my-2 text-[10px]">
            [BARCODE: {sample.invoiceNumber}]
          </div>
        )}

        <div className="text-center">
          {form.footer_text
            ? form.footer_text.split("\n").filter(Boolean).map((line, i) => <div key={i}>{line}</div>)
            : PRINTED_DEFAULT_FOOTER}
        </div>
      </div>
    </div>
  );
}

// ReceiptTemplateSettings is a structured toggle/text-field form, not a
// freeform drag-and-drop designer — mirrors models.ReceiptTemplate's own
// doc comment on the backend: a thermal receipt has almost no real layout
// freedom (lines top to bottom, only bold/center/cut as real formatting),
// so a fixed set of fields fully covers "customize what the receipt
// shows". branchId "" edits the global default row; picking a branch edits
// (or creates) that branch's own override. The live preview on the right
// (see ReceiptPreview above) needs no backend round-trip at all — it's a
// plain client-side render of the same fields against one fake sample
// order, so every change shows up instantly.
export default function ReceiptTemplateSettings() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("receipt_template");
  const { t: tCommon } = useTranslation("common");

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [form, setForm] = useState(DEFAULT_FORM);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => { getBranches(token).then((data) => setBranches(data || [])).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const branchOptions = useMemo(
    () => [{ value: "", label: t("branch_option_global_default", "Global default (every branch without its own override)") }, ...branches.map((b) => ({ value: String(b.id), label: b.name }))],
    [branches, t]
  );

  useEffect(() => {
    setFetching(true);
    getReceiptTemplate(token, branchId || undefined)
      .then((tmpl) => {
        setForm({
          show_logo: !!tmpl.show_logo,
          logo_path: tmpl.logo_path || "",
          header_text: tmpl.header_text || "",
          footer_text: tmpl.footer_text || "",
          show_tax_breakdown: tmpl.show_tax_breakdown !== undefined ? tmpl.show_tax_breakdown : true,
          show_cashier_name: tmpl.show_cashier_name !== undefined ? tmpl.show_cashier_name : true,
          show_customer_info: tmpl.show_customer_info !== undefined ? tmpl.show_customer_info : true,
          show_barcode: !!tmpl.show_barcode,
          show_order_number: tmpl.show_order_number !== undefined ? tmpl.show_order_number : true,
          show_invoice_number: tmpl.show_invoice_number !== undefined ? tmpl.show_invoice_number : true,
          show_date_time: tmpl.show_date_time !== undefined ? tmpl.show_date_time : true,
          label_subtotal: tmpl.label_subtotal || "",
          label_total: tmpl.label_total || "",
          label_payments: tmpl.label_payments || "",
          font_size: tmpl.font_size || "normal",
          paper_width_mm: String(tmpl.paper_width_mm || 80),
        });
        setLogoPreviewUrl(tmpl.logo_path ? getAssetUrl(tmpl.logo_path) : null);
      })
      .catch(() => notify.error(t("notify_load_failed", "Failed to load the receipt template")))
      .finally(() => setFetching(false));
  }, [branchId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Show the picked file instantly (no need to wait on the network for
    // the preview itself) — the upload below only needs to finish before
    // "Save Template" is clicked.
    setLogoPreviewUrl(URL.createObjectURL(file));
    setUploadingLogo(true);
    try {
      const result = await uploadReceiptLogo(token, file);
      setForm((prev) => ({ ...prev, logo_path: result.logo_path, show_logo: true }));
      notify.success(t("notify_logo_uploaded", "Logo uploaded"));
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_logo_upload_failed", "Failed to upload logo"));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setForm((prev) => ({ ...prev, logo_path: "", show_logo: false }));
    setLogoPreviewUrl(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await saveReceiptTemplate(token, {
        branch_id: branchId ? Number(branchId) : null,
        show_logo: form.show_logo,
        logo_path: form.logo_path,
        header_text: form.header_text,
        footer_text: form.footer_text,
        show_tax_breakdown: form.show_tax_breakdown,
        show_cashier_name: form.show_cashier_name,
        show_customer_info: form.show_customer_info,
        show_barcode: form.show_barcode,
        show_order_number: form.show_order_number,
        show_invoice_number: form.show_invoice_number,
        show_date_time: form.show_date_time,
        label_subtotal: form.label_subtotal.trim(),
        label_total: form.label_total.trim(),
        label_payments: form.label_payments.trim(),
        font_size: form.font_size,
        paper_width_mm: Number(form.paper_width_mm),
      });
      notify.success(t("notify_saved", "Receipt template saved"));
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  const PAPER_WIDTH_OPTIONS = [
    { value: "58", label: t("paper_width_58", "58mm") },
    { value: "80", label: t("paper_width_80", "80mm") },
  ];
  const FONT_SIZE_OPTIONS = [
    { value: "normal", label: t("font_size_normal", "Normal") },
    { value: "large", label: t("font_size_large", "Large") },
  ];

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{t("page_title", "Receipt Template")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("page_subtitle", "What prints on every thermal receipt — a template edit takes effect on the very next sale")}</p>
        </div>
      </PageHeader>

      {fetching ? (
        <div className="p-8 text-center text-gray-400">{t("loading_label", "Loading…")}</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
          <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
            <div className="space-y-6">
              <SelectBox label={t("field_branch_label", "Editing")} options={branchOptions} value={branchId} onChange={setBranchId} />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <SelectBox label={t("field_paper_width_label", "Paper Width")} options={PAPER_WIDTH_OPTIONS} value={form.paper_width_mm} onChange={(val) => setForm((prev) => ({ ...prev, paper_width_mm: val }))} />
                <SelectBox label={t("field_font_size_label", "Base Font Size")} options={FONT_SIZE_OPTIONS} value={form.font_size} onChange={(val) => setForm((prev) => ({ ...prev, font_size: val }))} />
              </div>

              <div>
                <p className="text-xs font-bold text-gray-500 mb-2">{t("field_logo_label", "Logo Image")}</p>
                <div className="flex items-center gap-4">
                  <div className="relative shrink-0">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden ring-4 ring-gray-50 bg-gray-100 flex items-center justify-center">
                      {logoPreviewUrl ? (
                        <img src={logoPreviewUrl} alt="Logo" className="w-full h-full object-contain" />
                      ) : (
                        <ICONS.ReceiptTemplate className="w-8 h-8 text-gray-300" />
                      )}
                    </div>
                    <label className="absolute -bottom-1 -right-1 bg-[var(--primary-color)] text-white p-1.5 rounded-xl shadow-lg cursor-pointer hover:scale-110 transition-transform">
                      <ICONS.Plus className="w-3.5 h-3.5" />
                      <input type="file" className="hidden" onChange={handleLogoChange} accept="image/png,image/jpeg,image/gif,image/webp" />
                    </label>
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-[10px] text-gray-400 font-medium">{t("logo_hint", "PNG, JPG, GIF or WEBP. Max 5MB.")}</p>
                    {uploadingLogo && <p className="text-[10px] text-blue-500 font-bold">{t("logo_uploading", "Uploading…")}</p>}
                    {logoPreviewUrl && !uploadingLogo && (
                      <button type="button" onClick={handleRemoveLogo} className="text-[10px] font-black uppercase text-red-400 hover:text-red-600 transition-colors text-left">
                        {t("btn_remove_logo", "Remove Logo")}
                      </button>
                    )}
                  </div>
                </div>
                {form.logo_path && (
                  <ToggleSwitch className="mt-3" label={t("field_show_logo_label", "Print this logo")} checked={form.show_logo} onChange={(val) => setForm((prev) => ({ ...prev, show_logo: val }))} />
                )}
              </div>

              <FloatingTextarea label={t("field_header_text_label", "Header text (optional)")} value={form.header_text} onChange={setField("header_text")} rows={2} />
              <FloatingTextarea label={t("field_footer_text_label", "Footer text (optional — defaults to \"Thank you!\")")} value={form.footer_text} onChange={setField("footer_text")} rows={2} />

              <div>
                <p className="text-xs font-bold text-gray-500 mb-2">{t("section_visibility_title", "What to show")}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ToggleSwitch label={t("field_show_order_number_label", "Show order number")} checked={form.show_order_number} onChange={(val) => setForm((prev) => ({ ...prev, show_order_number: val }))} />
                  <ToggleSwitch label={t("field_show_invoice_number_label", "Show invoice number")} checked={form.show_invoice_number} onChange={(val) => setForm((prev) => ({ ...prev, show_invoice_number: val }))} />
                  <ToggleSwitch label={t("field_show_date_time_label", "Show date & time")} checked={form.show_date_time} onChange={(val) => setForm((prev) => ({ ...prev, show_date_time: val }))} />
                  <ToggleSwitch label={t("field_show_tax_breakdown_label", "Show tax breakdown")} checked={form.show_tax_breakdown} onChange={(val) => setForm((prev) => ({ ...prev, show_tax_breakdown: val }))} />
                  <ToggleSwitch label={t("field_show_cashier_name_label", "Show cashier name")} checked={form.show_cashier_name} onChange={(val) => setForm((prev) => ({ ...prev, show_cashier_name: val }))} />
                  <ToggleSwitch label={t("field_show_customer_info_label", "Show customer info")} checked={form.show_customer_info} onChange={(val) => setForm((prev) => ({ ...prev, show_customer_info: val }))} />
                  <ToggleSwitch label={t("field_show_barcode_label", "Print a barcode (invoice number)")} checked={form.show_barcode} onChange={(val) => setForm((prev) => ({ ...prev, show_barcode: val }))} />
                </div>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-500 mb-2">{t("section_labels_title", "Custom Labels (leave blank for the default)")}</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FloatingInput label={t("field_label_subtotal_label", "\"Subtotal\" label")} value={form.label_subtotal} onChange={setField("label_subtotal")} />
                  <FloatingInput label={t("field_label_total_label", "\"Total\" label")} value={form.label_total} onChange={setField("label_total")} />
                  <FloatingInput label={t("field_label_payments_label", "\"Payments\" label")} value={form.label_payments} onChange={setField("label_payments")} />
                </div>
              </div>

              <div className="flex gap-4 pt-6 border-t border-gray-50">
                <Button variant="primary" onClick={handleSubmit} loading={loading} className="flex-grow shadow-lg shadow-blue-500/10" icon={ICONS.Save}>
                  {t("btn_save", "Save Template")}
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:sticky lg:top-6">
            <p className="text-xs font-bold text-gray-500 mb-2 px-1">{t("section_preview_title", "Live Preview")}</p>
            <ReceiptPreview form={form} logoPreviewUrl={logoPreviewUrl} t={t} />
            <p className="text-[10px] text-gray-400 mt-2 px-1 italic">{t("preview_hint", "Updates instantly as you change settings — mirrors what will actually print, using sample data")}</p>
          </div>
        </div>
      )}
    </div>
  );
}
